import {
  Delete as DeleteIcon,
  Image as ImageIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import React, { ChangeEvent, useState } from 'react';

import DownloadFileButton from '../DownloadFileButton';

const FormComponent: React.FC<{
  accept?: string;
  chooseDisabled?: boolean;
  deleteDisabled?: boolean;
  downloadPath?: string;
  fileName?: string;
  label?: string;
  multiple?: boolean;
  name: string;
  onChange?: (file: File) => void;
  onDelete?: () => void;
  viewPath?: string;
}> = ({
  accept,
  fileName = '',
  chooseDisabled = false,
  deleteDisabled = false,
  downloadPath,
  label = 'Upload File',
  name,
  multiple = false,
  onChange,
  onDelete,
  viewPath = '',
}) => {
  const [fileNameState, setFileNameState] = useState(fileName);

  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) {
      return;
    }
    const file = e.target.files[0];
    setFileNameState(file.name);

    onChange(file);
    e.target.value = '';
  };
  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      sx={{
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        border: '1px solid',
        borderColor: grey[400],
        borderRadius: 1,
        height: 86,
        px: 1,
        py: 0.5,
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="body2">{label}</Typography>
        {(fileNameState || downloadPath) && (
          <Stack direction="row" spacing={1} alignItems="center">
            {fileNameState && viewPath && (
              <Tooltip title="View File">
                <IconButton
                  aria-label="View File"
                  size="small"
                  onClick={() => window.open(viewPath)}
                >
                  <ImageIcon />
                </IconButton>
              </Tooltip>
            )}
            {downloadPath && <DownloadFileButton href={downloadPath} />}
            {fileNameState && (
              <Tooltip title="Delete File">
                <IconButton
                  aria-label="Delete File"
                  disabled={deleteDisabled}
                  size="small"
                  onClick={() => {
                    setFileNameState('');
                    onDelete?.();
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        )}
      </Box>
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        sx={{ minWidth: 0, width: '100%', overflow: 'hidden' }}
      >
        {fileNameState ? (
          <Typography
            variant="body2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {fileNameState}
          </Typography>
        ) : (
          <>
            <Button
              component="label"
              disabled={chooseDisabled}
              variant="outlined"
              size="small"
              startIcon={<UploadFileIcon />}
              sx={{ width: 'fit-content' }}
            >
              Choose File
              <input
                accept={accept}
                name={name}
                onChange={handleFileUpload}
                type="file"
                hidden
                multiple={multiple}
              />
            </Button>
            <Typography variant="body2">No file chosen</Typography>
          </>
        )}
      </Box>
      <Typography variant="caption" sx={{ color: 'grey.500' }}>
        Accepted file types: {accept}
      </Typography>
    </Box>
  );
};

export default FormComponent;
